import React, { useEffect, useRef, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import useAuth from "../../hooks/useAuth";
import { TaskStatusIcon } from "./TaskDetailPane";
import { dueState, formatDueDate, formatMinutes, initials } from "./taskUtils";
import { RepeatBadge } from "./RepeatField";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermission } from "../../hooks/usePermission";

const CARDS_PER_COLUMN = 30;

export function SectionTime({ tasks }) {
  const total = tasks.reduce((sum, t) => sum + (t.minutesLogged || 0), 0);
  if (!total) return null;
  return (
    <span
      className="tk-section-time"
      title="Time logged on tasks in this section"
    >
      ⏱ {formatMinutes(total)}
    </span>
  );
}
const MAX_CARD_PROJECTS = 2;

// A card's subtasks, open by default; each can be ticked off or opened
// without opening the parent card.
function CardSubtasks({ subtasks, onOpen, onToggle }) {
  const [open, setOpen] = useState(true);
  const done = subtasks.filter((s) => s.completed).length;
  const stop = (e) => e.stopPropagation();
  return (
    <div className="tk-card-subtasks" onClick={stop} onKeyDown={stop}>
      <button
        type="button"
        className="tk-card-subtasks-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className={`tk-caret ${open ? "open" : ""}`} aria-hidden="true">
          ▸
        </span>
        {done}/{subtasks.length} subtasks
      </button>
      {open && (
        <ul>
          {subtasks.map((sub) => (
            <li key={sub.id}>
              <TaskStatusIcon
                size="sm"
                task={sub}
                onToggle={() => onToggle(sub)}
              />
              <button
                type="button"
                className={`tk-card-subtask-name ${sub.completed ? "tk-done-text" : ""}`.trim()}
                onClick={() => onOpen?.(sub.id)}
              >
                {sub.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CardBody({ task, onToggle, onOpen }) {
  const { user } = useAuth();
  const due = dueState(task.dueDate, task.completed);
  const extraProjects = task.projects.length - MAX_CARD_PROJECTS;
  return (
    <>
      <div className="tk-card-top">
        <TaskStatusIcon size="sm" task={task} onToggle={() => onToggle(task)} />
        <span
          className={`tk-card-name ${task.completed ? "tk-done-text" : ""}`.trim()}
        >
          {task.name}
        </span>
      </div>
      {task.parentTask && (
        <div className="tk-row-meta tk-card-parent">
          ↳ {task.parentTask.name}
        </div>
      )}
      {task.projects.length > 0 && (
        <div className="tk-card-projects">
          {task.projects.slice(0, MAX_CARD_PROJECTS).map((p) => (
            <span key={p.id} className="tk-chip tk-chip-static">
              {p.name}
            </span>
          ))}
          {extraProjects > 0 && (
            <span className="tk-muted">+{extraProjects}</span>
          )}
        </div>
      )}
      {task.subtasks?.length > 0 && (
        <CardSubtasks
          subtasks={task.subtasks}
          onOpen={onOpen}
          onToggle={onToggle}
        />
      )}
      <div className="tk-card-foot">
        <span className={`tk-due ${due ? `due-${due}` : ""}`}>
          {formatDueDate(task.dueDate)}
        {task.recurrence && <RepeatBadge rule={task.recurrence} />}
        </span>
        <span className="tk-card-badges">
          {task.minutesLogged > 0 && (
            <span className="tk-pill" title="Time logged">
              ⏱ {formatMinutes(task.minutesLogged)}
            </span>
          )}
          {task.waitingOnCount > 0 && !task.completed && (
            <span
              className="tk-pill tk-pill-warn"
              title="Waiting on other tasks"
            >
              Waiting
            </span>
          )}
          {task.assignee && (
            <span
              className="tk-avatar tk-avatar-sm"
              title={task.assignee.id === user?.id ? "Me" : task.assignee.name}
            >
              {initials(task.assignee.name)}
            </span>
          )}
        </span>
      </div>
    </>
  );
}

function BoardCard({ task, selected, onOpen, onToggle }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task.id,
  });
  return (
    <div
      ref={setNodeRef}
      className={`tk-board-card ${selected ? "selected" : ""} ${isDragging ? "dragging" : ""}`.trim()}
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !isDragging) onOpen(task.id);
        listeners?.onKeyDown?.(e);
      }}
      {...attributes}
      onPointerDown={listeners?.onPointerDown}
      aria-roledescription="Draggable task"
      aria-label={task.name}
    >
      <CardBody task={task} onToggle={onToggle} onOpen={onOpen} />
    </div>
  );
}

function BoardColumn({
  section,
  tasks,
  target,
  selectedId,
  onOpen,
  onToggle,
  onCreateIn,
  activeTaskId,
  draftRequest,
}) {
  const droppable = Boolean(target);
  const { setNodeRef, isOver } = useDroppable({
    id: section.id,
    disabled: !droppable,
  });
  const [draft, setDraft] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const submitting = useRef(false);

  useEffect(() => {
    if (draftRequest?.sectionId === section.id) setDraft("");
  }, [draftRequest, section.id]);
  const visible = showAll ? tasks : tasks.slice(0, CARDS_PER_COLUMN);
  const canCreate = usePermission(PERMISSIONS.TASK_CREATE);
  const canAdd = canCreate && droppable && section.type !== "completed";

  const submit = async () => {
    const name = draft?.trim();
    if (!name) {
      setDraft(null);
      return;
    }
    // Clear before awaiting so a repeated Enter can't create a duplicate.
    if (submitting.current) return;
    submitting.current = true;
    setDraft("");
    const ok = await onCreateIn(section, name);
    submitting.current = false;
    if (!ok) setDraft(name);
  };

  const dropHint = !target
    ? "Can't drop here"
    : target.hint
      ? target.hint
      : target.completed
        ? "Drop to mark complete"
        : target.dueDate
          ? `Drop to set due ${formatDueDate(target.dueDate)}`
          : "Drop to clear the due date";

  return (
    <section
      ref={setNodeRef}
      className={`tk-board-col ${isOver ? "over" : ""} ${activeTaskId && !droppable ? "blocked" : ""}`.trim()}
      aria-label={`${section.name}, ${tasks.length} tasks`}
    >
      <header className="tk-board-col-head">
        <span className="tk-section-name">{section.name}</span>
        <span className="tk-count">{tasks.length}</span>
        <SectionTime tasks={tasks} />
        {canAdd && (
          <button
            type="button"
            className="tk-icon-btn tk-icon-btn-sm tk-board-add-icon"
            onClick={() => setDraft("")}
            aria-label={`Add task to ${section.name}`}
            title={`Add task to ${section.name}`}
          >
            +
          </button>
        )}
      </header>
      {activeTaskId && <div className="tk-board-drop-hint">{dropHint}</div>}
      <div className="tk-board-cards">
        {draft !== null && (
          <div className="tk-board-card tk-board-card-new">
            <textarea
              autoFocus
              rows={2}
              placeholder="Write a task name"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
                if (e.key === "Escape") setDraft(null);
              }}
              onBlur={() => !draft.trim() && setDraft(null)}
            />
          </div>
        )}
        {visible.map((task) => (
          <BoardCard
            key={task.id}
            task={task}
            selected={task.id === selectedId}
            onOpen={onOpen}
            onToggle={onToggle}
          />
        ))}
        {tasks.length > CARDS_PER_COLUMN && (
          <button
            type="button"
            className="tk-link-btn tk-board-more"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll
              ? "Show fewer"
              : `Show ${tasks.length - CARDS_PER_COLUMN} more`}
          </button>
        )}
        {tasks.length === 0 && draft === null && (
          <div className="tk-board-empty">No tasks</div>
        )}
      </div>
      {canAdd && draft === null && (
        <button
          type="button"
          className="tk-board-add"
          onClick={() => setDraft("")}
        >
          + Add task
        </button>
      )}
    </section>
  );
}

// Kanban board of My Tasks, one column per section. Dropping a card on a
// column applies that column's target (due date, no date, or complete).
export default function TaskBoard({
  groups,
  loading,
  selectedId,
  onOpen,
  onToggle,
  targetFor,
  onMove,
  onCreateIn,
  draftRequest,
}) {
  const [activeTask, setActiveTask] = useState(null);
  const sensors = useSensors(
    // A small threshold keeps a plain click on a card opening it.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    // Space picks a card up (Enter is reserved for opening it).
    useSensor(KeyboardSensor, {
      keyboardCodes: {
        start: ["Space"],
        cancel: ["Escape"],
        end: ["Space", "Enter"],
      },
    }),
  );

  if (loading) return <p className="tk-muted tk-list-msg">Loading tasks…</p>;

  const allTasks = groups.flatMap((g) => g.tasks);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={({ active }) =>
        setActiveTask(allTasks.find((t) => t.id === active.id) || null)
      }
      onDragCancel={() => setActiveTask(null)}
      onDragEnd={({ active, over }) => {
        setActiveTask(null);
        if (!over) return;
        const task = allTasks.find((t) => t.id === active.id);
        const group = groups.find((g) => g.section.id === over.id);
        if (!task || !group || group.tasks.some((t) => t.id === task.id))
          return;
        onMove(task, group.section);
      }}
    >
      <div className="tk-board">
        {groups.map(({ section, tasks }) => (
          <BoardColumn
            key={section.id}
            section={section}
            tasks={tasks}
            target={targetFor(section)}
            selectedId={selectedId}
            onOpen={onOpen}
            onToggle={onToggle}
            onCreateIn={onCreateIn}
            activeTaskId={activeTask?.id}
            draftRequest={draftRequest}
          />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>
        {activeTask && (
          <div className="tk-board-card tk-board-card-overlay">
            <CardBody task={activeTask} onToggle={() => {}} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
